import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { AppDialogSection, AppDialog, AppDialogContent } from '@/UI/AppDialog/AppDialog';
import { Fade } from '@/UI/Transitions/Fade';
import { H1, TextLgRg } from '@/UI/AppTypography/AppTypography';
import { Tag } from '@/UI/Tag/Tag';
import { I18N_KEYS } from '@/language/keys';
import { useWithLoader } from '@/hooks/useWithLoader';
import { color } from '@/colors';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { Flex, FlexColCenHor, FlexCenHorVer, FlexColCenVer } from '@/UI/styles';
import { useTour } from '../TourContext';
import type { TourStepMedia } from '../tourTypes';

interface StartOfTourModalProps {
  content: string;
  disallowClose?: boolean;
  media?: TourStepMedia;
  onClose?: () => void;
  onNextStep: () => Promise<void>;
  open: boolean;
  title: string;
}

const HEIGHT = 433;
const IMG_WIDTH = 288;

export const StartOfTourModal = ({
  content,
  disallowClose = false,
  media,
  onClose,
  onNextStep,
  open,
  title,
}: StartOfTourModalProps) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const { activeTour } = useTour();
  const { isLoading, execute: handleNextStep } = useWithLoader(onNextStep, { sync: true });

  const startTour = useCallback(() => {
    posthog.capture('product_tour_start', {
      tour: activeTour,
    });
    handleNextStep();
  }, [posthog, handleNextStep, activeTour]);

  const closeTour = useCallback(() => {
    posthog.capture('product_tour_skip', {
      tour: activeTour,
    });
    onClose?.();
  }, [posthog, onClose, activeTour]);

  return (
    <AppDialog
      open={open}
      onClose={disallowClose ? undefined : closeTour}
      slots={{
        transition: Fade,
      }}
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            maxWidth: 648,
            width: 648,
          },
        },
        transition: {
          timeout: 0,
        },
        backdrop: {
          slotProps: {
            transition: {
              timeout: 0,
            },
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <FlexCenHorVer
            sx={{
              maxHeight: '100%',
            }}
          >
            <FlexColCenVer
              sx={{
                px: 5.25,
                width: '360px',
                gap: 4,
              }}
            >
              <FlexColCenHor sx={{ gap: 2, alignItems: 'flex-start' }}>
                {isFigmaEmbedMode && (
                  <Tag
                    text={t(I18N_KEYS.FIGMA_EMBED.BETA_BADGE)}
                    bgColor={color.Black84}
                    textColor={color.White64_T}
                    disableTextTransform
                    sx={{
                      height: '16px',
                      borderRadius: '5px',
                      // UI3 body.medium typography (Tag defaults to a monospace label
                      // variant; override so the Beta badge matches Figma's UI3 type ramp).
                      fontFamily: '"DM Sans", system-ui, -apple-system, Arial, sans-serif',
                      fontSize: '11px',
                      fontWeight: 450,
                      lineHeight: '16px',
                      letterSpacing: '0.055px',
                    }}
                  />
                )}
                <H1>{title}</H1>
                <TextLgRg>{content}</TextLgRg>
              </FlexColCenHor>
              <Flex sx={{ gap: 2 }}>
                <ButtonContained size="small" loading={isLoading} onClick={startTour}>
                  {t(I18N_KEYS.PRODUCT_TOURS.START_OF_TOUR.START_NOW)}
                </ButtonContained>
                <ButtonContained size="small" mode="text" disabled={isLoading} onClick={closeTour}>
                  {t(I18N_KEYS.PRODUCT_TOURS.START_OF_TOUR.SKIP)}
                </ButtonContained>
              </Flex>
            </FlexColCenVer>
            <Flex
              sx={{
                width: IMG_WIDTH,
                height: HEIGHT,
              }}
            >
              <img
                src={
                  media?.default?.path
                    ? `${media.default.path.replace('/upload/', `/upload/c_fill,g_auto,h_${HEIGHT},w_${IMG_WIDTH}/`)}`
                    : ''
                }
                alt="Flow Tour"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Flex>
          </FlexCenHorVer>
        </AppDialogContent>
      </AppDialogSection>
    </AppDialog>
  );
};
